// Low-balance toast dispatcher for the AI credit usage prototype (#08).
// See plans/saas-billing/08-ai-credit-usage-display.md (Surface 3).
//
// Subscribes to the billing mock's AICreditBalance stream and fires Ionic
// toasts at the 20%, 5%, and 0% remaining-percent thresholds. Each toast
// fires AT MOST ONCE per (tenantId, billingCycleStart, threshold) tuple to
// avoid nag fatigue. Dedup keys live in localStorage (per spec) so a page
// reload in the same cycle does not re-trigger a previously-shown toast; the
// keys naturally age out when billingCycleStart rolls forward.
//
// The 0% toast suppresses automatically when topUpBalance > 0 because the
// `remainingCredits` field from #01 already folds in topUpBalance (per spec).

import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { of, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

import { AICreditBalance } from '../models/billing';
import { IBillingMockService } from './billing-mock/billing-mock.interface';
import { BILLING_SERVICE } from './billing-mock/billing.token';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';

interface ThresholdConfig {
  /** Remaining-percent boundary the balance must cross BELOW to fire. */
  percent: number;
  /** Stable label used in the dedup key + logging. */
  label: 'twenty' | 'five' | 'zero';
  color: 'warning' | 'danger';
  durationMs: number;
}

// Ordered from MOST URGENT (lowest percent) to least urgent so a single
// emission fires the SMALLEST applicable threshold per evaluation. A balance
// at 3% must fire 5% (or 0% if depleted) -- never 20%. If both 5% and 0%
// would fire on the same emission, we only want the 0% one (more urgent,
// more accurate copy). (M7 fix.)
const THRESHOLDS: readonly ThresholdConfig[] = [
  { percent: 0, label: 'zero', color: 'danger', durationMs: 12000 },
  { percent: 5, label: 'five', color: 'warning', durationMs: 10000 },
  { percent: 20, label: 'twenty', color: 'warning', durationMs: 8000 }
] as const;

const DEDUP_KEY_PREFIX = 'solo_ai_credit_toast';

@Injectable({ providedIn: 'root' })
export class AICreditToastDispatcherService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private started = false;

  constructor(
    @Inject(BILLING_SERVICE) private readonly billing: IBillingMockService,
    private readonly authService: AuthService,
    private readonly toastController: ToastController,
    private readonly router: Router,
    private readonly logger: LoggerService
  ) {}

  /**
   * Begin watching the AI credit balance stream and dispatching toasts. Safe
   * to call multiple times; subsequent calls are no-ops.
   */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.authService.authSession$
      .pipe(
        distinctUntilChanged((a, b) => a.token === b.token),
        switchMap(session => session.token ? this.billing.getAICreditBalance() : of(null)),
        takeUntil(this.destroy$)
      )
      .subscribe(balance => {
        if (balance === null) {
          return;
        }
        this.evaluate(balance);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private evaluate(balance: AICreditBalance): void {
    if (balance.allocatedCredits <= 0) {
      return;
    }
    // Per spec: remainingPercent = (allocated - used - reserved) / allocated * 100.
    // Top-up balance is folded into the 0% suppression naturally (remainingCredits
    // includes it, so remainingCredits === 0 implies the top-up is also zero).
    const cycleRemaining = Math.max(
      0,
      balance.allocatedCredits - balance.usedCredits - balance.reservedCredits
    );
    const remainingPercent = (cycleRemaining / balance.allocatedCredits) * 100;

    // Walk thresholds most-urgent -> least-urgent. Fire the FIRST one we've
    // crossed below that hasn't already fired this cycle.
    for (const threshold of THRESHOLDS) {
      const crossed =
        threshold.percent === 0
          ? balance.remainingCredits === 0
          : remainingPercent < threshold.percent;
      if (!crossed) {
        continue;
      }
      const key = this.dedupKey(balance, threshold.label);
      if (this.hasFired(key)) {
        return;
      }
      this.markFired(key);
      void this.fireToast(threshold, balance, remainingPercent);
      return;
    }
  }

  private dedupKey(balance: AICreditBalance, label: ThresholdConfig['label']): string {
    return `${DEDUP_KEY_PREFIX}:${balance.tenantId}:${balance.billingCycleStart}:${label}`;
  }

  private hasFired(key: string): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(key) !== null;
  }

  private markFired(key: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(key, '1');
  }

  private async fireToast(
    threshold: ThresholdConfig,
    balance: AICreditBalance,
    remainingPercent: number
  ): Promise<void> {
    const message = this.copyFor(threshold, balance, remainingPercent);
    const toast = await this.toastController.create({
      message,
      duration: threshold.durationMs,
      color: threshold.color,
      position: 'bottom',
      cssClass: `ai-credit-toast ai-credit-toast--${threshold.label}`,
      buttons: [
        {
          text: 'Buy more credits',
          role: 'info',
          handler: () => {
            this.router.navigateByUrl('/billing/credits/top-up');
            return true;
          }
        }
      ]
    });
    await toast.present();
    this.logger.info(
      'AICreditToastDispatcherService',
      'fireToast',
      'low-balance toast presented',
      { threshold: threshold.label, remainingPercent: Math.round(remainingPercent) }
    );
  }

  private copyFor(
    threshold: ThresholdConfig,
    balance: AICreditBalance,
    _remainingPercent: number
  ): string {
    if (threshold.label === 'twenty') {
      return "Heads up — you've used 80% of your AI credits this cycle.";
    }
    if (threshold.label === 'five') {
      return 'Almost out of AI credits. Buy more to avoid interruption.';
    }
    // 0% threshold. Differentiate copy when top-up balance is positive (which
    // shouldn't happen because remainingCredits includes top-up, but we keep
    // the branch as a defensive guard against future model changes).
    if (balance.topUpBalance > 0) {
      return 'AI credits depleted — using top-up balance.';
    }
    const refreshDate = formatShortDate(balance.billingCycleEnd);
    return `You're out of AI credits for this cycle. New credits available ${refreshDate}, or top up now.`;
  }
}

/**
 * Format an ISO date string as "MMM d" (e.g., "Jun 23"). Returns the original
 * string when parsing fails so the toast still renders something sensible.
 */
function formatShortDate(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return iso;
  }
  return new Date(ts).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
}
